import { ComponentProps, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { SuperTextarea } from '@frontend/super-textarea';
import type { GetWeavePopNotificationByType, NotificationActions } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Text, IconButton, styles, Chip } from '@frontend/design-system';
import { useNotificationContext } from '../notification-provider';
import { getTitleFromPayload } from '../notification-utils';
import { useNewNotificationsContext } from '../shared/new-notifications-context';
import { BaseNotificationComponentProps, BaseNotificationInner, BaseNotificationOuter } from './base';
import {
  DefaultCloseNotificationButton,
  DefaultNotificationBody,
  DefaultNotificationBodyDefault,
  DefaultNotificationBodyWrapper,
  DefaultNotificationHeader,
  DefaultNotificationInnerWrapper,
} from './defaults/default-notification';

type Props = BaseNotificationComponentProps<GetWeavePopNotificationByType<'sms-message-new'>>;

export const SmsMessageNotificationComponent = ({ isHistorical, notification, emit, ...rest }: Props) => {
  const [isResponding, setIsResponding] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [currentResponse, setCurrentResponse] = useState<string>();
  const notificationTrayContext = useNotificationContext();
  const { t } = useTranslation();
  const markRead = () =>
    notificationTrayContext.markThreadNotificationsAsRead(
      notification.payload.threadId,
      notification.payload.locationId
    );
  const actions: NotificationActions = [
    {
      label: t('View'),
      action: () => {
        emit({ action: 'view', payload: undefined }, notification);
        markRead();
      },
    },
    ...(notification.flags.shouldAllowReply
      ? [
          {
            label: t('Reply'),
            action: () => {
              setIsResponding(true);
              markRead();
            },
          },
        ]
      : []),
  ];

  const send = () => {
    if (currentResponse) {
      emit({ action: 'respond', payload: currentResponse }, notification);
      setIsSent(true);
    }
    //timeout here to give the user time to process the "sent" message before the notification closes
    setTimeout(() => {
      emit({ action: 'timed-out', payload: undefined }, notification);
    }, 1200);
  };

  return (
    <BaseNotificationOuter
      actions={actions}
      notification={{
        ...notification,
        state: { ...notification.state, paused: notification.state.paused || isResponding },
      }}
      emit={emit}
      isHistorical={isHistorical}
      {...rest}
    >
      {!isResponding ? (
        <BaseNotificationInner
          onClose={() => emit({ action: 'ignore', payload: undefined }, notification)}
          actions={actions}
          title={t('New Message')}
          body={
            <SmsMessageNotificationBody
              name={getTitleFromPayload(notification.payload)}
              body={notification.payload.body}
              locationPhone={notification.payload.locationPhone}
              locationPhoneDisplayName={notification.payload.locationPhoneDisplayName}
            />
          }
          id={notification.id}
          location={notification.location}
          timestamp={notification.timestamp}
          notificationType={notification.type}
          {...rest}
        />
      ) : (
        // because the BaseNotificationInner has a very rigid structure, i have to opt out here and compose my own with its pieces
        // @TODO: it would be good to provide some alternative BaseNotificationInner that allow more variety so we don't have to do this
        <div css={{ position: 'relative' }}>
          <DefaultCloseNotificationButton
            onClick={() => emit({ action: 'ignore', payload: undefined }, notification)}
          />
          <DefaultNotificationHeader
            title={getTitleFromPayload(notification.payload)}
            timestamp={notification.timestamp}
            onClose={() => emit({ action: 'ignore', payload: undefined }, notification)}
          />
          <DefaultNotificationInnerWrapper hasActions={false}>
            {!isSent ? (
              <>
                <DefaultNotificationBodyWrapper>
                  <DefaultNotificationBody
                    name={getTitleFromPayload(notification.payload)}
                    body={notification.payload.body}
                  />
                </DefaultNotificationBodyWrapper>
                <SmsMessageNotificationReply
                  response={currentResponse || ''}
                  onChange={setCurrentResponse}
                  onSubmit={send}
                  back={() => setIsResponding(false)}
                />
              </>
            ) : (
              <div
                css={css`
                  padding: ${theme.spacing(2)};
                `}
              >
                <Text>Message Sent!</Text>
              </div>
            )}
          </DefaultNotificationInnerWrapper>
        </div>
      )}
    </BaseNotificationOuter>
  );
};

type SmsNotificationBodyProps = {
  onChange: (text: string) => void;
  onSubmit: () => void;
  back: () => void;
  response: string;
};

const SmsMessageNotificationReply = ({ response, onChange, onSubmit, back }: SmsNotificationBodyProps) => {
  const onTextareaChange = (text: string) => {
    onChange(text);
  };
  const isNewNotificationsExperience = useNewNotificationsContext();

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      css={[
        isNewNotificationsExperience
          ? css`
              margin-top: ${theme.spacing(2)};
            `
          : css`
              border-top: 1px solid ${theme.colors.neutral30};
            `,
      ]}
    >
      <SuperTextarea
        css={css`
          background: ${theme.colors.white};
          border-radius: ${theme.borderRadius.medium};
          border: none;
        `}
        disableEmojis={true}
        SubmitComponent={Send}
        hasSubmit
        autoFocus
        value={response}
        extraActions={[
          {
            icon: <Icon name='back-small' color='primary' />,
            label: 'Back',
            onClick: back,
            showLabelOnHover: true,
          },
        ]}
        onChange={onTextareaChange}
        onSubmit={() => {
          onSubmit();
          console.log('Submitting');
        }}
      />
    </div>
  );
};

const Send: ComponentProps<typeof SuperTextarea>['SubmitComponent'] = ({ disabled, onClick }) => (
  <IconButton label='Send' disabled={disabled} onClick={onClick}>
    <Icon name='send' color='primary' />
  </IconButton>
);

const SmsMessageNotificationBody = ({
  name,
  body,
  locationPhone,
  locationPhoneDisplayName,
}: {
  name: string;
  body: string;
  locationPhone: string;
  locationPhoneDisplayName?: string;
}) => {
  const newNotificationsExperience = useNewNotificationsContext();

  if (!newNotificationsExperience) {
    return <DefaultNotificationBodyDefault name={name} body={body} />;
  }

  return (
    <>
      {name && (
        <Text size='medium' weight='bold'>
          {name}
        </Text>
      )}
      {locationPhone && (
        <Chip
          css={{ fill: theme.colors.neutral50, maxWidth: 150, fontSize: theme.font.size.small }}
          leftElement={<Icon name='fax-number' size={14} />}
          variant='warningDark'
          tooltipLabel={formatPhoneNumber(locationPhone)}
        >
          {locationPhoneDisplayName || formatPhoneNumber(locationPhone, true)}
        </Chip>
      )}
      <Text size='medium' css={styles.multilineTruncate(2)}>
        {body}
      </Text>
    </>
  );
};
